import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo } from 'react'

import TanstackTable from 'components/reusable/table/TanstackTable'
import { type ChartConfig } from 'components/ui/chart'

import NeedsAttentionPieChart from './NeedsAttentionPieChart'
import { type NAVStatisticsProps, type StatisticsType } from './types'
import { colors } from '../../../../../config/tailwind/colors'

const chartConfig: ChartConfig = {
  reason: {
    label: 'Reason',
  },
  items: {
    label: 'NAV Items',
  },
  delinquency_risk: {
    label: 'Delinquency Risk',
    color: colors.primitive.thatch[800],
  },
  under_payment: {
    label: 'Under Payment',
    color: colors.primitive.thatch[700],
  },
  over_payment: {
    label: 'Over Payment',
    color: colors.primitive.thatch[600],
  },
  unexpected_amount: {
    label: 'Unexpected Amount',
    color: colors.primitive.thatch[500],
  },
  missing_enrollment: {
    label: 'Missing Enrollment',
    color: colors.primitive.thatch[400],
  },
  automation_reconciliation: {
    label: 'Automation Reconciliation',
    color: colors.primitive.thatch[300],
  },
  incorrect_configuration: {
    label: 'Incorrect Configuration',
    color: colors.primitive.gray[800],
  },
  payments_issue: {
    label: 'Payments Issue',
    color: colors.primitive.gray[700],
  },
  backdate_requested: {
    label: 'Backdate Requested',
    color: colors.primitive.gray[600],
  },
  offboarding: {
    label: 'Offboarding',
    color: colors.primitive.gray[500],
  },
  other: {
    label: 'Other',
    color: colors.primitive.gray[400],
  },
}

function getChartDataFromStatistics (
  byReasonStats: Record<string, number> | undefined
): StatisticsType[] {
  return Object.entries(byReasonStats || {}).map(([key, value]) => ({
    reason: key,
    items: value,
  }))
}

export const NAVReasonPieChart = ({
  stats: byReasonStats,
}: NAVStatisticsProps) => {
  const chartData: StatisticsType[] = useMemo(() => {
    return getChartDataFromStatistics(byReasonStats)
  }, [byReasonStats])

  return (
    <NeedsAttentionPieChart
      title='Reason'
      nameKey='reason'
      dataKey='items'
      chartData={chartData}
      chartConfig={chartConfig}
    />
  )
}

const columnHelper = createColumnHelper<StatisticsType>()

const columns = [
  columnHelper.accessor('reason', {
    header: 'Reason',
    cell: (info) => chartConfig[info.getValue() as string].label,
  }),
  columnHelper.accessor('items', {
    header: () => <div className='text-end'>Count</div>,
    cell: (info) => <div className='text-end'>{info.getValue()}</div>,
  }),
]

export const NAVReasonTable = ({
  stats: byReasonStats,
}: NAVStatisticsProps) => {
  const chartData: StatisticsType[] = useMemo(
    () => getChartDataFromStatistics(byReasonStats),
    [byReasonStats]
  )

  const table = useReactTable({
    data: chartData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex: 0, pageSize: 5 } },
  })

  return (
    <div>
      <div className='flex items-center justify-center mb-4'>
        <div className='font-semibold'>Reason</div>
      </div>
      <TanstackTable
        table={table}
        paginationLabelSingular='item'
        showPageSelectionIfSinglePage={false}
      />
    </div>
  )
}
